<template>
  <!-- eslint-disable vue/no-v-text -->
  <div class="p-6 bg-white dark:bg-gray-800 min-h-screen">
    <!-- Header -->
    <div class="mb-6">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-4">
        <div>
          <h1
            class="text-3xl font-bold text-gray-900 dark:text-white mb-2"
            v-text="'API Dependency Graph'"
          ></h1>
          <p class="text-gray-600 dark:text-gray-400">
            Query a workload to see which APIs it calls internally
          </p>
        </div>
        <!-- Environment Toggle -->
        <div class="flex items-center gap-3">
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
            >Environment:</span
          >
          <div class="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                selectedEnvironment === 'staging'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              ]"
              @click="changeEnvironment('staging')"
            >
              Staging
            </button>
            <button
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                selectedEnvironment === 'production'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              ]"
              @click="changeEnvironment('production')"
            >
              Production
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Section -->
    <div class="mb-6">
      <label
        for="workload-search-input"
        class="sr-only"
        >Enter workload name</label
      >
      <div class="relative">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            class="h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          id="workload-search-input"
          ref="searchInputRef"
          v-model="searchQuery"
          type="text"
          class="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Enter workload name (e.g., automation-ad-publishing-facebook-ad-processor-worker)"
          @keydown.enter.prevent="searchWorkload"
        />
        <button
          v-if="searchQuery"
          class="absolute inset-y-0 right-0 pr-3 flex items-center"
          @click="clearSearch"
        >
          <svg
            class="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="loading"
      class="flex items-center justify-center h-96"
    >
      <div class="text-center">
        <div
          class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"
        ></div>
        <p class="text-gray-600 dark:text-gray-400">Loading dependency data...</p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
    >
      <div class="flex items-center">
        <svg
          class="w-5 h-5 text-red-600 dark:text-red-400 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p class="text-red-800 dark:text-red-200">{{ error }}</p>
      </div>
      <button
        v-if="searchQuery.trim()"
        class="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        @click="searchWorkload"
      >
        Retry
      </button>
    </div>

    <!-- Syntax Error State -->
    <div
      v-else-if="workloadDependencies && workloadDependencies.syntaxErrorFound"
      class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-8 text-center"
    >
      <svg
        class="mx-auto h-12 w-12 text-red-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
        Invalid Workload Name Format
      </h3>
      <p class="text-sm text-gray-600 dark:text-gray-400">
        The workload name
        <span class="font-mono font-medium">{{ workloadDependencies.workloadName }}</span> is not in
        the correct format.
      </p>
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">
        Workload names must end with <span class="font-mono font-medium">-api</span> or
        <span class="font-mono font-medium">-worker</span>.
      </p>
    </div>

    <!-- Workload Not Found State -->
    <div
      v-else-if="workloadDependencies && !workloadDependencies.workloadFound"
      class="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-8 text-center"
    >
      <svg
        class="mx-auto h-12 w-12 text-orange-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Workload Not Found</h3>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        The workload
        <span class="font-mono font-medium">{{ workloadDependencies.workloadName }}</span> was not
        found in the cluster.
      </p>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Please double-check that you have entered the correct workload name.
      </p>
      <a
        href="https://console.cloud.google.com/kubernetes/workload/overview?project=highlevel-backend"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
      >
        <svg
          class="w-4 h-4 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
        Verify in Google Cloud Console
      </a>
    </div>

    <!-- Istio Proxy Not Found (Already Moved Off) -->
    <div
      v-else-if="workloadDependencies && !workloadDependencies.istioProxyFound"
      class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-8 text-center"
    >
      <svg
        class="mx-auto h-12 w-12 text-green-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">🎉 Congratulations!</h3>
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Thank you! You have already moved
        <span class="font-mono font-medium">{{ workloadDependencies.workloadName }}</span> away from
        Istio mesh.
      </p>
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">
        This workload doesn't make any internal API calls, so it no longer needs the istio-proxy
        sidecar.
      </p>
    </div>

    <!-- Results Section -->
    <div
      v-else-if="workloadDependencies && workloadDependencies.apis.length > 0"
      class="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
    >
      <!-- Header with Stats -->
      <div
        class="p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800"
      >
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div class="flex-1">
            <div class="flex items-center gap-3 mb-2">
              <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                {{ workloadDependencies.workloadName }}
              </h2>
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
              >
                {{ selectedEnvironment }}
              </span>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              This workload calls
              <span class="font-medium text-blue-600 dark:text-blue-400">{{
                workloadDependencies.apis.length
              }}</span>
              API{{ workloadDependencies.apis.length !== 1 ? 's' : '' }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <button
              class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2 shadow-sm hover:shadow transition-shadow"
              :title="copiedAll ? 'Copied!' : 'Copy all API names'"
              @click="copyAllApis"
            >
              <svg
                v-if="!copiedAll"
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <svg
                v-else
                class="w-4 h-4 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span>{{ copiedAll ? 'Copied!' : 'Copy All' }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- API Search Filter -->
      <div class="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <label
          for="api-search-input"
          class="sr-only"
          >Search APIs</label
        >
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              class="h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            id="api-search-input"
            v-model="apiSearchQuery"
            type="text"
            class="block w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Search APIs..."
          />
          <button
            v-if="apiSearchQuery"
            class="absolute inset-y-0 right-0 pr-3 flex items-center"
            @click="apiSearchQuery = ''"
          >
            <svg
              class="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- API List -->
      <div class="p-6">
        <div class="grid gap-3">
          <div
            v-for="api in filteredApis"
            :key="api"
            class="flex items-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 hover:shadow-md transition-all duration-200 group"
          >
            <div
              class="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-full flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-200"
            >
              <svg
                class="w-5 h-5 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-white truncate font-mono">
                {{ api }}
              </p>
            </div>
            <div class="flex-shrink-0 ml-4 flex items-center gap-2">
              <button
                class="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors opacity-0 group-hover:opacity-100"
                :title="copiedApi === api ? 'Copied!' : 'Copy API name'"
                @click="copyApiName(api)"
              >
                <svg
                  v-if="copiedApi !== api"
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <svg
                  v-else
                  class="w-4 h-4 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </button>
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
              >
                API Service
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="filteredApis.length === 0 && apiSearchQuery"
          class="text-center py-8 text-gray-500 dark:text-gray-400"
        >
          <p>No APIs match your search "{{ apiSearchQuery }}"</p>
        </div>
      </div>
    </div>

    <!-- No Results / Empty State (for -worker with empty APIs) -->
    <div
      v-else-if="
        workloadDependencies &&
        workloadDependencies.apis.length === 0 &&
        workloadDependencies.workloadFound
      "
      class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-8 text-center"
    >
      <svg
        class="mx-auto h-12 w-12 text-yellow-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
        No API dependencies found
      </h3>
      <p class="text-sm text-gray-600 dark:text-gray-400">
        The workload
        <span class="font-mono font-medium">{{ workloadDependencies.workloadName }}</span> does not
        call any APIs internally.
      </p>
      <div
        v-if="workloadDependencies.workloadName.endsWith('-worker')"
        class="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-left"
      >
        <div class="flex items-start">
          <span class="text-xl mr-3">⚠️</span>
          <div class="flex-1">
            <p class="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
              This workload needs to move away from Istio mesh
            </p>
            <ul class="text-xs text-blue-800 dark:text-blue-300 space-y-1.5 list-none">
              <li>
                🚀 <strong>Fewer pod restarts:</strong> Say goodbye to istio-proxy crashing parties!
                Your pods will finally get some peace and quiet.
              </li>
              <li>
                💾 <strong>Memory savings:</strong> Free up that precious RAM that istio-proxy was
                hoarding like a digital dragon. Your cluster will thank you!
              </li>
              <li>
                💰 <strong>Cost savings:</strong> No istio-proxy sidecar = less CPU usage per pod.
                Your cloud bill will do a happy dance! 💃
              </li>
              <li>
                🎯 <strong>Mesh efficiency:</strong> Less traffic = happier mesh. Think of it as
                decluttering your service mesh's social calendar.
              </li>
              <li>
                ⚡ <strong>Better performance:</strong> No API calls means no need for the mesh
                overhead. It's like removing unnecessary middlemen from your conversations!
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Initial State -->
    <div
      v-else
      class="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center"
    >
      <svg
        class="mx-auto h-16 w-16 text-gray-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Search for a workload</h3>
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Enter a workload name above to see which APIs it calls internally
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable vue/no-v-text */
import { ref, computed, onUnmounted } from 'vue'
import { dependencyGraphService } from '../../services/dependency-graph'
import type { WorkloadDependencies } from '../../types/dependency-graph'
import { logger } from '../../utils/logger'

// Component name for Vue DevTools
// @vue/component
defineOptions({
  name: 'DependencyGraphPage'
})

const DEFAULT_ENVIRONMENT = 'production' as const

const loading = ref(false)
const error = ref<string | null>(null)
const searchQuery = ref('')
const workloadDependencies = ref<WorkloadDependencies | null>(null)
const searchInputRef = ref<HTMLElement | null>(null)
const selectedEnvironment = ref<'staging' | 'production'>(DEFAULT_ENVIRONMENT)
const apiSearchQuery = ref('')
const copiedApi = ref<string | null>(null)
const copiedAll = ref(false)

const fetchWorkloadDependencies = async (workload: string) => {
  if (!workload.trim()) {
    return
  }

  try {
    loading.value = true
    error.value = null
    workloadDependencies.value = null

    const data = await dependencyGraphService.getWorkloadDependencies(
      workload.trim(),
      selectedEnvironment.value
    )

    // Validate data structure
    if (
      !data ||
      !data.workloadName ||
      typeof data.syntaxErrorFound !== 'boolean' ||
      typeof data.workloadFound !== 'boolean' ||
      typeof data.istioProxyFound !== 'boolean' ||
      !Array.isArray(data.apis)
    ) {
      throw new Error('Invalid response format from API')
    }

    workloadDependencies.value = data

    logger.log('Loaded dependencies for workload:', data.workloadName, 'APIs:', data.apis.length)
  } catch (err) {
    logger.error('Failed to fetch workload dependencies:', err)
    error.value = err instanceof Error ? err.message : 'Failed to fetch workload dependencies'
    workloadDependencies.value = null
  } finally {
    loading.value = false
  }
}

const changeEnvironment = (environment: 'staging' | 'production') => {
  if (selectedEnvironment.value !== environment) {
    selectedEnvironment.value = environment
    // If we have a workload loaded, reload it with new environment
    if (workloadDependencies.value?.workloadName) {
      fetchWorkloadDependencies(workloadDependencies.value.workloadName)
    }
  }
}

const searchWorkload = () => {
  if (!searchQuery.value.trim()) {
    return
  }

  const workload = searchQuery.value.trim()
  fetchWorkloadDependencies(workload)
}

const clearSearch = () => {
  searchQuery.value = ''
  workloadDependencies.value = null
  error.value = null
  apiSearchQuery.value = ''
}

// Filter APIs based on search query
const filteredApis = computed(() => {
  if (!workloadDependencies.value?.apis) return []
  if (!apiSearchQuery.value.trim()) return workloadDependencies.value.apis

  const query = apiSearchQuery.value.toLowerCase()
  return workloadDependencies.value.apis.filter((api) => api.toLowerCase().includes(query))
})

// Copy API name to clipboard
const copyApiName = async (api: string) => {
  try {
    await navigator.clipboard.writeText(api)
    copiedApi.value = api
    setTimeout(() => {
      copiedApi.value = null
    }, 2000)
  } catch (err) {
    logger.error('Failed to copy API name:', err)
  }
}

// Copy all API names to clipboard
const copyAllApis = async () => {
  if (!workloadDependencies.value?.apis) return

  try {
    const allApis = workloadDependencies.value.apis.join('\n')
    await navigator.clipboard.writeText(allApis)
    copiedAll.value = true
    setTimeout(() => {
      copiedAll.value = false
    }, 2000)
  } catch (err) {
    logger.error('Failed to copy all APIs:', err)
  }
}

onUnmounted(() => {
  // Cleanup if needed
})
</script>

<style scoped>
/* Custom scrollbar for suggestions */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: #4b5563;
}
</style>
